import PT from 'prop-types';
import React, { useState } from 'react';

import { Button } from '~/eds/src/components/Button/Button';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { useIntl } from 'react-intl';

import FlexLayout from '../FlexLayout/FlexLayout';
import Folder, { folderShape } from '../Folder/Folder';
import FolderModal from '../FolderModal/FolderModal';
import { folderTreePropType } from '../FolderTree/FolderTree';

function FolderInput({
  disabled,
  folder: initialFolder,
  folderTreeProps,
  id,
  isLoading,
  modalTitle,
  placeholder,
  onChange,
}) {
  const intl = useIntl();
  const [showModal, setShowModal] = useState(false);
  const [draftFolder, setDraftFolder] = useState(initialFolder);
  const [folder, setFolder] = useState(draftFolder);

  const handleChange = (_updatedValue, updatedFolder) =>
    setDraftFolder(updatedFolder);

  const handleSelectFolder = () => {
    setFolder(draftFolder);
    onChange(draftFolder);
  };

  const handleHideModal = () => setShowModal(false);

  const handleShowModal = () => setShowModal(true);

  return (
    <>
      <FlexLayout
        alignItems="center"
        justifyContent="space-between"
        disabled={disabled || isLoading}
        px={4}
        py={3}
        space={5}
        sx={{
          border: 'border',
          borderRadius: 'm',
          height: 'input-height',
        }}
      >
        <Folder
          shouldTruncate
          emptyFolderMessage={
            placeholder ??
            intl.formatMessage({
              id: 'FolderInput.selectFolder',
              defaultMessage: 'Select a folder',
              description: 'Default placeholder text for folder selection',
            })
          }
          folder={folder}
        />
        {isLoading ? (
          <Icon icon="loading" />
        ) : (
          <Button
            id={id}
            text={
              folder
                ? intl.formatMessage({
                    id: 'FolderInput.changeButton',
                    defaultMessage: 'Change',
                    description: 'Button text to change selected folder',
                  })
                : intl.formatMessage({
                    id: 'FolderInput.selectButton',
                    defaultMessage: 'Select',
                    description: 'Button text to select a folder',
                  })
            }
            variant="action"
            onClick={handleShowModal}
          />
        )}
      </FlexLayout>
      <FolderModal
        disabled={!draftFolder}
        folderTreeProps={{
          ...folderTreeProps,
          value: folder?.id,
          onChange: handleChange,
        }}
        title={modalTitle}
        visible={showModal}
        modalWidth="m"
        onHide={handleHideModal}
        onChange={handleChange}
        onSelectFolder={handleSelectFolder}
      />
    </>
  );
}

FolderInput.propTypes = {
  /** Disables the input */
  disabled: PT.bool,
  /** Adds an id attribute */
  id: PT.string,
  /** FolderTree props */
  folderTreeProps: PT.shape(folderTreePropType).isRequired,
  /** A Folder object */
  folder: PT.shape(folderShape),
  /** Title of the folder modal */
  modalTitle: PT.string,
  /** Placeholder message if folder is empty */
  placeholder: PT.string,
  /** Callback capturing selected folder object */
  onChange: PT.func,
};

export default FolderInput;
